/**
 * PerformanceConsolidator — Direct DOM injection for performance meta tags + CWV inline styles.
 * No longer uses react-helmet-async.
 */
import { useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContextNew';
import { setMetaHttpEquiv, setMetaName } from '@/lib/seo-dom';

export function PerformanceConsolidator() {
  const { language } = useLanguage();

  useEffect(() => {
    // Permissions-Policy
    setMetaHttpEquiv('Permissions-Policy', 'accelerometer=(), camera=(), geolocation=(self), gyroscope=(), magnetometer=(), microphone=(), payment=(), usb=(), interest-cohort=()');
    
    // Client Hints
    setMetaHttpEquiv('Accept-CH', 'DPR, Width, Viewport-Width, ECT, RTT, Downlink');
    
    // Security headers
    setMetaHttpEquiv('X-Content-Type-Options', 'nosniff');
    setMetaHttpEquiv('Referrer-Policy', 'strict-origin-when-cross-origin');
    
    // DNS prefetch control
    setMetaHttpEquiv('X-DNS-Prefetch-Control', 'on');
    
    // Color scheme
    setMetaName('color-scheme', 'light dark');

    // CWV inline styles
    const styleId = 'perf-consolidator-cwv';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `@font-face { font-display: swap!important; }
 img[loading="lazy"] { contain-intrinsic-size: 300px 200px; }.glass-card,.section-container { contain: layout style; }
 [role="banner"] { min-height: 100svh; contain: layout; }
 img { max-width: 100%; height: auto; }
 img:not([width]) { aspect-ratio: 16/9; }
 button, a, [role="button"] { touch-action: manipulation; }
 section:nth-child(n+4) { content-visibility: auto; contain-intrinsic-size: auto 600px; }
 * { -webkit-tap-highlight-color: transparent; }
 input, textarea, select { font-size: 16px; }
 body { text-rendering: optimizeSpeed; }
 [class*="fixed"], [class*="sticky"] { will-change: transform; contain: layout style; } `;
      document.head.appendChild(style);
    }

    // Speculation Rules
    const specId = 'perf-speculation-rules';
    if (!document.getElementById(specId)) {
      const specScript = document.createElement('script');
      specScript.id = specId;
      specScript.type = 'speculationrules';
      specScript.textContent = JSON.stringify({
        prefetch: [{
          urls: [`/${language}/services`, `/${language}/services/bpo`, `/${language}/services/digital-studio`, `/${language}/pricing`, `/${language}/contact`, `/${language}/start`],
          eagerness: 'moderate',
        }],
        prerender: [{
          urls: [`/${language}/start`, `/${language}/contact`],
          eagerness: 'conservative',
        }],
      });
      document.head.appendChild(specScript);
    }

    // Preload critical assets
    const preloadId = 'preload-logo';
    if (!document.getElementById(preloadId)) {
      const preload = document.createElement('link');
      preload.id = preloadId;
      preload.rel = 'preload';
      preload.href = '/favicon.svg';
      preload.as = 'image';
      preload.type = 'image/webp';
      document.head.appendChild(preload);
    }

    // Resource hints for critical navigation targets
    const prefetchTargets = [`/${language}/start`, `/${language}/contact`];
    document.querySelectorAll('link[data-seo="nav-prefetch"]').forEach(el => el.remove());
    for (const href of prefetchTargets) {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = href;
      link.setAttribute('data-seo', 'nav-prefetch');
      document.head.appendChild(link);
    }
  }, [language]);

  return null;
}
